import { useMemo } from 'react';
import get from 'lodash/get';

import { useOverviewRowsBuilder } from '../../../../utils/appointments/useOverviewRowsBuilder';
import {
  getInlineCellData,
  visitOverviewAdapter,
} from '../../utils/appointment';
import {
  INLINE_TYPES,
  APPOINTMENT_STATUSES,
  VIRTUAL_VISIT_TYPE_CODE,
  FORBIDDEN_VISIT_TYPES_FOR_MAP,
  OFFICE_VISIT_TYPE_CODE,
  TELEMEDICINE_VISIT_TYPE_CODE,
} from '../../../../constants';
import getVisitTypeName from '../../../../utils/appointments/getVisitTypeName';
import { checkForIsSignedAllConsents } from '../VisitConsents/utils/checkForIsSignedAllConsents';
import { STATUS_SURVEY } from '../VisitSurveyListView/constants';

export function useDetailsRows({
  provider,
  isUpcoming,
  appointment,
  formatMessage,
  isUrgentVisit,
  providerContext,
  prescribedMedications,
}) {
  const status = get(appointment, 'status.coreAppName');
  const icsAllowed = get(appointment, 'status.icsUrlAllowed');
  const icsUrl = get(appointment, 'icsUrl');
  const visitTypeCode = get(appointment, 'visitType.code');
  const appointmentFormattedDuration = get(appointment, 'formattedDuration');
  const isRequiredSurvey = get(appointment, 'requiredSurveys', false);
  const isSignedAllConsents = checkForIsSignedAllConsents(appointment);

  const isEirTelemedicineVisitType =
    visitTypeCode === TELEMEDICINE_VISIT_TYPE_CODE;

  const notSignedPreVisits = get(appointment, 'surveyAssignments', []).filter(
    form =>
      ![STATUS_SURVEY.COMPLETED, STATUS_SURVEY.EXPIRED].includes(form.status) &&
      !!form.survey.required,
  );

  const isAvailablePrescriptionSection = get(
    appointment,
    'prescriptionTabAvailability',
    false,
  );

  const {
    makeDocRow,
    makeProviderRow,
    makeReasonRow,
    makeDateRow,
    makeSurveyRow,
    makeEditablePaymentRow,
    makeConsentRow,
    makeNotificationConsentRow,
    makePrescriptionsRow,
  } = useOverviewRowsBuilder({ formatMessage });

  const service = get(appointment, 'service');
  const code = get(appointment, 'visitType.code');
  const location = get(appointment, 'location', {});
  const { geofenceLat, geofenceLng } = location;

  const isInlineAppointment = INLINE_TYPES.includes(code);

  const isVirtualVisit = VIRTUAL_VISIT_TYPE_CODE === code;

  const isForbiddenMap =
    FORBIDDEN_VISIT_TYPES_FOR_MAP.includes(code) ||
    !(geofenceLat && geofenceLng);

  const icsVisitTypeCodeAllowed = [
    OFFICE_VISIT_TYPE_CODE,
    VIRTUAL_VISIT_TYPE_CODE,
  ];

  const { schedule, reason } = visitOverviewAdapter(appointment);
  const { time } = schedule;
  const resolvedReason = {
    ...reason,
    type: getVisitTypeName({
      sourceOfCreation: get(appointment, 'sourceOfCreation'),
      defaultValue: get(reason, 'type'),
      formatMessage,
      visitTypeCode: code,
    }),
  };

  const DATE_ROW = useMemo(
    () =>
      makeDateRow({
        time,
        status,
        icsUrl,
        icsAllowed:
          icsAllowed && icsVisitTypeCodeAllowed.includes(visitTypeCode),
        additionalDuration: appointmentFormattedDuration,
        visitTypeCode: code,
      }),
    [time, appointmentFormattedDuration, status, code],
  );

  const REASON_ROW = useMemo(() => makeReasonRow(resolvedReason), [
    resolvedReason,
  ]);

  const PRESCRIPTION_ROW = useMemo(
    () => makePrescriptionsRow(prescribedMedications),
    [prescribedMedications],
  );

  const PROVIDER_ROW = useMemo(() => {
    const hasLocation = !isUrgentVisit;
    const options = isInlineAppointment
      ? {
          renderCell: () => getInlineCellData(provider, formatMessage),
        }
      : { service, isVirtualVisit };

    return makeProviderRow({
      provider,
      hasLocation,
      ...options,
      isForbiddenMap,
    });
  }, [provider, service, isInlineAppointment]);

  const DOC_ROW = useMemo(() => makeDocRow({ providerContext, appointment }), [
    providerContext,
    appointment,
  ]);

  const SURVEY_ROW = useMemo(() => makeSurveyRow({ appointment }), [
    appointment,
  ]);

  const CONSENT_ROW = useMemo(
    () => makeConsentRow({ appointment, providerContext }),
    [appointment, providerContext],
  );

  const NOTIFICATION_ROW = useMemo(
    () => makeNotificationConsentRow({ appointment, providerContext }),
    [appointment, providerContext],
  );

  const extraRows = providerContext
    ? [
        makeEditablePaymentRow({
          appointment,
          isUpcoming,
        }),
      ]
    : [];

  const showNotificationRow = useMemo(
    () => !providerContext && status === APPOINTMENT_STATUSES.UPCOMING,

    [providerContext, status],
  );

  const rows = isInlineAppointment
    ? [
        ...(showNotificationRow && [NOTIFICATION_ROW]),
        DATE_ROW,
        PROVIDER_ROW,
        REASON_ROW,
        ...(isAvailablePrescriptionSection && [PRESCRIPTION_ROW]),
        SURVEY_ROW,
        DOC_ROW,
      ]
    : [
        ...((!isSignedAllConsents ||
          (isRequiredSurvey && notSignedPreVisits.length > 0)) && [
          NOTIFICATION_ROW,
        ]),
        DATE_ROW,
        ...(isEirTelemedicineVisitType ? [] : [PROVIDER_ROW]),
        REASON_ROW,
        ...(isAvailablePrescriptionSection && [PRESCRIPTION_ROW]),
        ...extraRows,
        SURVEY_ROW,
        DOC_ROW,
        CONSENT_ROW,
      ];

  return {
    rows,
  };
}



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/AppointmentDetailsPopup/useDetailsRows.jsx